import type { AgentProfile } from '../../services/agentService';

/** A catalog specialist (the built-in roster, from getAgentProfiles). */
export type Specialist = AgentProfile;

/**
 * A conversation member — a catalog specialist (profile_id set) or a user-created
 * one (carries its own system_prompt/tier). Stored on the conversation so custom
 * members work end-to-end.
 */
export interface MemberSpec {
  id: string;
  name: string;
  persona: string;
  avatar_icon: string;
  /** Owner-set group-specific display name (overrides `name` in this chat). */
  alias?: string | null;
  /** Owner-set per-group context that augments the agent's persona for THIS chat only. */
  context?: string | null;
  /** Storage path of an owner-generated avatar image (overrides `avatar_icon`). */
  avatar_path?: string | null;
  /** Drive file id of the avatar when the chat's media lives in the owner's cloud drive. */
  avatar_drive_file_id?: string | null;
  profile_id?: string | null;
  /** Set when this member is a user's persistent custom agent (links knowledge/tools). */
  custom_agent_id?: string | null;
  system_prompt?: string | null;
  model_tier?: 'SMART' | 'EXPERT' | 'RESEARCH' | null;
  temperature?: number | null;
  tools?: string[];
  knowledge_backend?: 'none' | 'simple' | 'vector';
}

/** One message in a conversation. `author` is 'you' or a member id. */
export interface ChatMsg {
  id: string;
  /** Client-generated id for an optimistic user echo; the server echoes it back so the bubble
   *  reconciles by id (not content). Undefined on server/agent/system messages. */
  clientMsgId?: string;
  author: 'you' | string;
  /** For human messages: which human sent it (multi-human chats). */
  authorUid?: string;
  authorName: string;
  avatarIcon?: string;
  content: string;
  /** Optional generative-UI widget spec (rendered inline). Wired for later. */
  spec?: unknown;
  /** Files the user attached as enquiry references. */
  attachments?: import('../../services/conversationService').MessageAttachment[];
  /** Emoji reactions on this bubble (with originator name for hover attribution). */
  reactions?: import('../../services/conversationService').MessageReaction[];
  /** Owner-bookmarked (pinned) message. */
  bookmarked?: boolean;
  /** Language this message is in (a code, e.g. 'ko'); non-English → shows a "See translation" link. */
  language?: string;
  /** Cached translations by target-language code (e.g. { en: '…' }). */
  translations?: Record<string, string>;
  /** Cached translations of this message's generative-UI spec (the choice chips), keyed by
   *  language code; each value is the translated spec as a JSON string. */
  spec_translations?: Record<string, string>;
  /** Clickable web-source citations when the reply was grounded on a live web lookup (title + url). */
  citations?: { title: string; url: string }[];
  /** Storage path of the generated voice-over for this message (present once read-aloud has run) — replays are
   *  free, so its presence lets the UI show a persistent audio bubble instead of re-charging to regenerate. */
  voicePath?: string;
  voiceMime?: string;
  ts: number;
}

/** A human in the chat — the owner plus anyone invited via a link. */
export interface HumanMember {
  uid: string;
  name: string;
  role: 'owner' | 'member';
}

/** A conversation = the owner (+ invited humans) + 1–8 specialist members. */
export interface ChatSession {
  id: string;
  title: string;
  slug?: string;
  ownerId?: string;
  members: MemberSpec[];
  humanMembers?: HumanMember[];
  storageLocation?: 'cloud' | 'drive';
  logoUrl?: string | null;
  language?: string | null;
  /** Group toggle: each agent replies in its own native language. */
  agents_native_language?: boolean;
  messages: ChatMsg[];
  createdAt: number;
  updatedAt: number;
}

export const GROUP_MIN = 1;
// Max AI specialists in one chat. Raised 8 → 12 so a full media-production crew (directors + composer + writer +
// legal) can co-work in one room. Keep this in sync with the backend cap in functions/src/api/handlers/conversations.ts.
export const GROUP_MAX = 12;
